import type {
  GraphQLClientGlobalOptions,
  GraphQLClientOptions,
  TypedDocumentNode,
  Variables,
} from './types';
import { base } from './base';

const globalOptions: Omit<GraphQLClientGlobalOptions, 'endpointUrl'> = {
  headers: {},
  onError: (error: unknown) => {
    throw error;
  },
};

export interface CreateGraphQLClientOptions {
  /**
   * Headers bound to this client — layered over the global headers, still
   * overridable per request. Read on every request, so a getter works.
   */
  headers?: Record<string, string>;
}

/**
 * Builds a client bound to one endpoint, and to the headers that endpoint needs.
 *
 * `endpointUrl` is a full URL, built the same way `init`'s endpoint is:
 * `` `${apiBaseURL}/aggregates/v1/accountancy_practice/graphql` ``. Omit it for the
 * configured endpoint. The client is built on the first request, so this may be
 * called before `init`.
 *
 * Bind `headers` whenever the endpoint authenticates differently from the default
 * one, so the credentials travel with the endpoint instead of relying on every
 * call site to pass them.
 */
export function createGraphQLClient(
  endpointUrl?: string,
  clientOptions: CreateGraphQLClientOptions = {}
) {
  // oxlint-disable-next-line require-await
  return async <T, V extends Variables = Variables>(
    document: TypedDocumentNode<T, V>,
    variables: V,
    options: GraphQLClientOptions = {}
  ): Promise<T> => {
    const requestHeaders =
      options.requestHeaders instanceof Headers
        ? options.requestHeaders
        : new Headers(options.requestHeaders);

    // Precedence: per-request > bound to the client > global. Applied in that
    // order because `Headers.has` is case-insensitive and only gaps get filled.
    [clientOptions.headers, globalOptions.headers].forEach(headers => {
      Object.entries(headers ?? {}).forEach(([key, value]) => {
        if (!requestHeaders.has(key)) {
          requestHeaders.set(key, value);
        }
      });
    });

    const mergedOptions = {
      ...options,
      requestHeaders,
      endpointUrl,
    };

    try {
      if (globalOptions.wrapper) {
        return globalOptions.wrapper(base<T, V>(document, variables, mergedOptions));
      }
      return base<T, V>(document, variables, mergedOptions);
    } catch (error) {
      globalOptions.onError(error);
    }
  };
}

export const graphQLClient = Object.assign(createGraphQLClient(), {
  init: (options: GraphQLClientGlobalOptions): void => {
    Object.defineProperties(globalOptions, Object.getOwnPropertyDescriptors(options));
    base.init(options.endpointUrl);
  },
});
